<template>
  <div class="">

    <div class="flex items-center">
      <div class="text-lg mr-4">
        Send this to client
      </div>

      <NoteIcon page="submit" />
    </div>

    <div class="mt-3 text-base text-gray-700">
      You can now send this form to this client. 
      Please make sure you check each of the following.
    </div>
    
    <div class="my-8">

      <div class="p-4 mb-16 bg-yellow-100 border border-yellow-500 text-base text-gray-800 rounded w-full lg:w-1/2 flex items-center" 
        v-if="broker.docusign_link != null">
        <input type="checkbox" v-model="signElectronicLink" id="input-sign-electronic-link" />
        <label for="input-sign-electronic-link" class="ml-2">
          Include a link to electronically sign my Privacy and Consent form.
        </label>
      </div>

      <div class="overflow-hidden">
        <div class="float-left rounded-full px-4 py-2 bg-gray-300 text-gray-800 text-lg font-light">1</div>
        <div class="mt-2" style="margin-left:60px;">
          <div class="text-lg font-light">Documents</div>

          <template v-if="numTotalDocuments == null">
          </template>

          <template v-else>
            <template v-if="numTotalDocuments > 0">

              <template v-for="(applicantDocuments, index) in documentsByApplicants">
                <div class="mt-8 text-sm font-semibold border-b mb-1 pb-1">{{ applicantDocuments.applicantName }}</div>

                <template v-if="applicantDocuments.documents.length > 0">
                  <template v-for="(document, documentIndex) in applicantDocuments.documents">
                    <div class="mt-1 text-sm text-gray-600">{{ document.type }}</div>
                  </template>
                </template>

                <template v-else>
                  <div class="mt-1 text-sm text-gray-600">No documents selected</div>
                </template>
              </template>

            </template>

            <template v-else>
              <div class="mt-4 p-4 bg-red-100 border border-red-500 text-sm text-gray-800 rounded w-full lg:w-1/2">
                You have not selected any documents for your client to upload.<br/>
                You can do this via 
                <router-link 
                  :to="{ 
                    name  : 'application-document', 
                    params: { 
                      clientId   : $route.params.clientId,
                      applicantId: $store.state[$store.state.view + 'Module'].client.applicants[0].id
                    } 
                  }" 
                  tag="a"
                  class="text-blue-500 underline">Documents</router-link>
                page.
              </div>
            </template>
          </template>
        </div>
      </div>


      <div class="overflow-hidden mt-16">
        <div class="float-left rounded-full px-4 py-2 bg-gray-300 text-gray-800 text-lg font-light">2</div>
        <div class="mt-2" style="margin-left:60px;">
          <div class="text-lg font-light">Display the Following Pages</div>
          <div class="text-sm mt-1 text-gray-600">
            Depending on the client, you may wish to hide some pages which are not relevant.
          </div>
          
          <div class="mt-4 flex items-center">
            <input type="checkbox" value="requirements" v-model="pages" id="input-page-requirements" />
            <label for="input-page-requirements" class="w-56 text-sm ml-2">Requirements</label>
          </div>

          <div class="form-line flex items-center">
            <input type="checkbox" value="assets" v-model="pages" id="input-page-assets" />
            <label for="input-page-assets" class="text-sm ml-2">Assets</label>
          </div>

          <div class="form-line flex items-center">
            <input type="checkbox" value="liabilities" v-model="pages" id="input-page-liabilities" />
            <label for="input-page-liabilities" class="text-sm ml-2">Liabilities</label>
          </div>

          <div class="form-line flex items-center">
            <input type="checkbox" value="expenses" v-model="pages" id="input-page-expenses" />
            <label for="input-page-expenses" class="text-sm ml-2">Living Expenses</label>
          </div>

          <div class="form-line flex items-center">
            <input type="checkbox" value="risk-profiles" v-model="pages" id="input-page-risk-profiles" />
            <label for="input-page-risk-profiles" class="text-sm ml-2">Risk Profiles</label>
          </div>

          <div class="form-line flex items-center">
            <input type="checkbox" value="documents" v-model="pages" id="input-page-documents" :disabled="numTotalDocuments == 0" />
            <label for="input-page-documents" class="text-sm ml-2">Documents</label>

            <template v-if="numTotalDocuments == 0">
              <span class="text-gray-600 px-3">/</span>
              <span class="text-red-500">You have not selected any documents for your client to upload</span>
            </template>
          </div>
        </div>
      </div>



      <div class="overflow-hidden mt-16 w-full lg:w-1/2">
        <div class="float-left rounded-full px-4 py-2 bg-gray-300 text-gray-800 text-lg font-light">3</div>
        <div class="mt-2" style="margin-left:60px;">
          <div class="text-lg font-light">Email</div>
          
          <div class="mt-4 text-sm">Send to the primary applicant's email address</div>

          <div class="form-line">
            <input type="text" class="input-standard" v-model="toEmail" />
            <div v-if="toEmailError != null" class="mt-2 text-red-600">{{ toEmailError }}</div>
          </div>

          <div class="flex items-center label-line">
            <div class="text-sm mr-4">
              Send a copy to the following emails
            </div>

            <a href="#" @click.prevent="addCCEmail" class="text-blue-500 hover:underline">Add an email</a>
          </div>

          <div class="mt-1 text-gray-600 text-sm">
            Your (the broker) email and other applicants' are automatically listed below. You can add more email addresses if you wish.
          </div>

          <template v-for="(cc, index) in ccEmails">
            <div class="form-line relative">
              <input type="text" class="input-standard" v-model="cc.email" />
              <a href="#" @click.prevent="removeCCEmail(index)" class="text-blue-500 hover:underline">
                <svg width="16" height="16" stroke-width="2" class="absolute text-gray-500 hover:text-red-500" stroke="currentColor" fill="none" style="top:13px;right:10px;">
                  <use xlink:href="/static/img/feather-sprite.svg#x-circle"/>
                </svg>
              </a>
              <div v-if="cc.error != null" class="mt-2 text-red-600">{{ cc.error }}</div>
            </div>
          </template>
        </div>
      </div>





      <div class="overflow-hidden mt-16">
        <div class="float-left rounded-full px-4 py-2 bg-gray-300 text-gray-800 text-lg font-light">4</div>
        <div class="mt-2" style="margin-left:60px;">
          <div class="text-lg font-light">SMS - Optional</div>

          <div class="text-sm mt-1 text-gray-600">
            You can pre-define the following settings in your
            <router-link
              :to="{
                name  : 'broker-notifications',
                params: {
                  brokerId : broker.id
                }
              }"
              tag="a"
              class="text-blue-500 hover:underline">Notifications Settings</router-link>.
          </div>

          <div class="mt-4 flex items-center">
            <input type="checkbox" v-model="smsClient" id="input-sms-client" 
              :disabled="$store.state[$store.state.view + 'Module'].client.applicants[0].person.mobile_phone == null" />
            <label for="input-sms-client" class="text-sm ml-2">Send a SMS message to client notifying them that an email has been sent.</label>
          </div>

          <div class="text-red-500 ml-5" v-if="$store.state[$store.state.view + 'Module'].client.applicants[0].person.mobile_phone == null">
            Primary applicant's mobile phone has not been provided.
          </div>


          <div class="form-line flex items-center">
            <input type="checkbox" v-model="smsBroker" id="input-sms-broker" />
            <label for="input-sms-broker" class="text-sm ml-2">Receive a SMS message when client completes the form.</label>
          </div>
        </div>
      </div>




      <div class="overflow-hidden mt-16 w-full lg:w-1/2">
        <div class="float-left rounded-full px-4 py-2 bg-gray-300 text-gray-800 text-lg font-light">5</div>
        <div class="mt-2" style="margin-left:60px;">
          <div class="text-lg font-light">Email Content</div>
          <div class="mt-4 text-sm flex items-center">
            Subject

            <span class="text-gray-600 ml-4">
              You can pre-define the subject in &nbsp;
            </span>
            <router-link
              :to="{
                name: 'broker-mail-setting',
                params: {
                  brokerId: broker.id
                }
              }"
              tag="a"
              class="text-blue-500 hover:underline">Mail Settings</router-link>.
          </div>

          <div class="form-line">
            <input type="text" class="input-standard" v-model="subject" />
            <div v-if="subjectError != null" class="mt-2 text-red-600">{{ subjectError }}</div>
          </div>

          <div class="label-line flex items-center">
            <div class="text-sm">Personal message to client</div>

            <a href="#"
              class="text-gray-700 underline ml-4"
              @click.prevent="resetPersonalMessage">Reset to my default</a>
          </div>

          <div class="mt-2">
            <textarea
              class="w-full border border-gray-600 p-3 outline-none text-sm"
              style="height:300px;"
              v-model="personalMessage"
              @keyup="personalMessageChanged"></textarea>
          </div>

          <div class="mt-2 text-gray-700 text-sm">
            <template v-if="clientLatestSubmit != null && clientLatestSubmit.id != null">
              The above message was added in the last email that was sent to this client.
              Feel free to change if you need to.
              You can also reset it to your default personal message saved in your 
              <router-link :to="{
                name : 'broker-mail-setting',
                params : {
                  brokerId : broker.id
                }
              }"
              tag="a"
              class="text-blue-600 no-underline hover:underline">Mail settings</router-link> page.
            </template>

            <template v-else>
              The above message, which is saved in your
              <router-link :to="{
                name : 'broker-mail-setting',
                params : {
                  brokerId : broker.id
                }
              }"
              tag="a"
              class="text-blue-600 no-underline hover:underline">Mail settings</router-link> page
              will be added. You can further customize this message for this client below if you need to.
            </template>
          </div>
        </div>
      </div>







      <div class="overflow-hidden mt-16 w-full lg:w-1/2">
        <div class="float-left rounded-full px-4 py-2 bg-gray-300 text-gray-800 text-lg font-light">6</div>
        <div class="mt-2" style="margin-left:60px;">
          <div class="text-lg font-light">Attachments</div>

          <template v-if="$store.state[$store.state.view + 'Module'].broker.mailto == 0">
            <template v-if="brokerAttachments == null"></template>

            <template v-else>
              <template v-if="brokerAttachments.length > 0">
                
                <div class="mt-8 text-sm">Select default files to attach</div>

                <template v-for="(attachment, index) in brokerAttachments">
                  <div class="mt-2 bg-gray-300 p-2 border flex items-center">
                    <div class="text-blue-500 mr-4">
                      <input type="checkbox" 
                        checked="checked"
                        v-on:change="toggleDefaultAttachment(attachment.id, $event.target.checked)"
                        :id="'inputDefaultAttachment' + index" />
                    </div>

                    <div class="text-blue-500">
                      <svg width="16" height="16" stroke-width="2" class="align-middle" stroke="currentColor" fill="none">
                        <use xlink:href="/static/img/feather-sprite.svg#paperclip"/>
                      </svg>
                    </div>

                    <div class="ml-2">
                      <label :for="'inputDefaultAttachment' + index">{{ attachment.file_name }}</label>
                    </div>
                  </div>
                </template>
              </template>

              <div class="mt-8 text-sm">Additional Attachments</div>

              <template v-if="clientAttachments.length > 0">
                <template v-for="(attachment, index) in clientAttachments">
                  <div class="mt-2 bg-gray-300 p-2 border overflow-hidden">
                    <div class="float-left text-blue-500">
                      <svg width="16" height="16" stroke-width="2" class="align-middle" stroke="currentColor" fill="none">
                        <use xlink:href="/static/img/feather-sprite.svg#paperclip"/>
                      </svg>
                    </div>

                    <div class="float-left ml-2">
                      {{ attachment.file_name }}
                    </div>

                    <div class="float-right">
                      <a href="#" 
                        class="text-xs text-gray-700 no-underline hover:underline"
                        @click.prevent="deleteClientAttachment(attachment.id, index)">Delete</a>
                    </div>
                  </div>
                </template>
              </template>         

            </template>

            <div class="mt-4">
              <label for="inputFileNewAttachment" class="text-blue-600 hover:underline cursor-pointer text-sm">Upload another attachment</label>
              <input class="hidden" ref="inputFile" id="inputFileNewAttachment" type="file" v-on:change="uploadClientAttachment($event)"/>
            </div>
          </template>
        </div>
      </div>


      

      <div class="mt-12 mb-6">
        <template v-if="$store.state[$store.state.view + 'Module'].broker.mailto == 0">
          <button 
            v-on:click="preview" 
            class="text-white outline-none bg-green-500 hover:bg-green-600 inline-block overflow-hidden font-semibold text-sm py-3 w-32">Preview email</button>

          &nbsp;&nbsp;

          <button 
            v-on:click="submit" 
            class="text-white outline-none bg-blue-500 hover:bg-blue-600 inline-block overflow-hidden font-semibold text-sm py-3 w-32">Send</button>
        </template>

        <template v-else>
          <button 
            v-on:click="submit" 
            class="text-white outline-none bg-blue-500 hover:bg-blue-600 inline-block overflow-hidden font-semibold text-sm py-3 w-32">
            Open my email client to send
          </button>
        </template>
      </div>
    </div>
  </div>
</template>

<script>
  import axios            from 'axios'
  import InputText        from '@/shared/components/form/InputText'
  import TitleSelector    from '@/shared/components/form/TitleSelector'
  import TextareaField    from '@/shared/components/form/TextareaField'
  import { EventBus }     from '@/shared/event-bus'
  import PageHeader       from '@/shared/components/headers/PageHeader'
  import validator        from 'validator'
  import NoteIcon         from '@/shared/components/NoteIcon'

  export default {

    components: {
      InputText,
      TextareaField,
      TitleSelector,
      PageHeader,
      NoteIcon
    },

    data () {
      return {
        broker               : _.cloneDeep(this.$store.state[this.$store.state.view + 'Module'].broker),
        documentsByApplicants: null,
        pages                : ['requirements', 'assets', 'liabilities', 'expenses', 'risk-profiles', 'documents'],
        brokerAttachments    : [],
        clientAttachments    : [],
        selectedAttachmentIds: [],
        personalMessage      : null,
        clientLatestSubmit   : null,
        
        toEmail              : _.cloneDeep(this.$store.state[this.$store.state.view + 'Module'].client.applicants[0].person.email),
        toEmailError         : null,
        
        ccEmails             : null,
        
        smsClient            : false,
        smsBroker            : false,
        
        subject              : null,
        subjectError         : null,
        
        notificationSettings : null,
        
        numTotalDocuments    : null,
        signElectronicLink   : false
      }
    },

    created() {
      this.getDocuments()

      // get the default value for subject
      if(this.broker.email_subject == null) {
        this.subject = this.broker.first_name + " " + this.broker.last_name + " has an application for you to complete"
      }

      else {
        this.subject = this.broker.email_subject
      }

      this.apiGet({
        brokerId : this.broker.id
      }, 'broker/attachments', (attachments) => {
        this.brokerAttachments = attachments
        for(let i=0; i < attachments.length; i++) {
          this.selectedAttachmentIds.push(attachments[i].id)
        }
      })

      // also get client attachments
      this.apiGet({
        brokerId : this.broker.id,
        clientId : this.$route.params.clientId
      }, 'broker/attachments', (attachments) => {
        this.clientAttachments = attachments
        for(let i=0; i < attachments.length; i++) {
          this.selectedAttachmentIds.push(attachments[i].id)
        }
      })

      // get the client's personal message
      this.apiGet({
        clientId : this.$route.params.clientId
      }, 'client/latest-submit', (submit) => {
        this.clientLatestSubmit = submit

        if(submit != null && submit.id != null) {
          this.personalMessage = this.clientLatestSubmit.personal_message
        } else {
          this.personalMessage = this.broker.personal_message
        }

        // check if there is a personal message in local storage
        let pm = window.localStorage.getItem(this.broker.id + '-' + this.$route.params.clientId + '-pm')
        if(pm != null) {
          this.personalMessage = pm
        }
      })

      // get broker's notification settings
      this.apiGet({
        brokerId : this.broker.id
      }, 'broker/notification-settings', (settings) => {
        this.smsClient = settings.sms_client_when_broker_submits
        this.smsBroker = settings.sms_broker_when_client_submits
      })

      // set the CC Emails
      this.ccEmails = [{
        email : this.$store.state.user.email,
        error : null
      }]

      for(let i=0; i < this.$store.state[this.$store.state.view + 'Module'].client.applicants.length; i++) {
        let app = this.$store.state[this.$store.state.view + 'Module'].client.applicants[i]

        if(i > 0 && app.person.email != null) {
          this.ccEmails.push({
            email : app.person.email, 
            error : null
          })
        }
      }
    },

    methods : {

      // we want to prevent from losing all the updated message, so we will store it in
      // local storage for now
      personalMessageChanged() {
        let s = window.localStorage
        s.setItem(this.broker.id + '-' + this.$route.params.clientId + '-pm', this.personalMessage)
      },

      addCCEmail() {
        this.ccEmails.push({
          email : null,
          error : null
        })
      },

      removeCCEmail(index) {
        this.ccEmails.splice(index, 1)
      },

      toggleDefaultAttachment(attachmentId, checked) {
        if(checked) {
          this.selectedAttachmentIds.push(attachmentId)
        } else {
          this.selectedAttachmentIds = _.without(this.selectedAttachmentIds, attachmentId)
        }

        this.selectedAttachmentIds = _.uniq(this.selectedAttachmentIds)
      },

      getDocuments() {
        this.showLoader("Loading documents...")
        this.apiGet({
          clientId: this.$route.params.clientId,
        }, 'document/applicant-documents', (data) => {
          this.hideLoader()
          this.numTotalDocuments = data.documents.length

          // take documents out since there are no documents
          if(this.numTotalDocuments == 0) {
            this.pages = _.without(this.pages, 'documents')
          }

          // organize the response by applicants
          let arr = []
          for(let i=0; i < this.$store.state[this.$store.state.view + 'Module'].client.applicants.length; i++) {
            let applicant = this.$store.state[this.$store.state.view + 'Module'].client.applicants[i]
            let documents = []

            for(let j=0; j < data.documents.length; j++) {
              if(data.documents[j].applicant_id == applicant.id) {
                documents.push(data.documents[j])
              }
            }

            arr.push({
              applicantName: this.getApplicantName(applicant),
              documents    : documents
            })
          }

          this.documentsByApplicants = arr
        })
      },

      uploadClientAttachment($event) {
        let file     = $event.target.files[0] || $event.dataTransfer.files[0]
        let formData = new FormData()
        formData.append('file', file)
        formData.append('clientId', this.$route.params.clientId)
        this.showLoader("Uploading...")

        axios.post(process.env.VUE_APP_API_URL + '/api/broker/upload-attachment', formData, {
          headers: {
            'Content-Type'  : 'multipart/form-data',
            'Authorization' : 'Bearer ' + localStorage.getItem('bp-broker-jwt')
          }
        }).then(response => {
          this.hideLoader()
          this.clientAttachments.push(response.data)
          this.$refs.inputFile.value = null
          this.selectedAttachmentIds.push(response.data.id)
        }).catch(function(err) {

        });
      },

      deleteClientAttachment(attachmentId, index) {
        this.$modal.show('confirm-modal', {
          // width : 600,
          title  : "Please confirm your action",
          message: "Are you sure you want to delete this?",
          buttons:[{
            label          : 'Yes',
            color          : 'red',
            loadingViaLocal: true, // a bit of HACK, we want to show loading image when button is clicked, but hard to pass loading prop to a modal
            action         : () => {
              this.apiDelete({
                fileId : attachmentId
              }, 'broker/delete-attachment', () => {
                this.$modal.hide('confirm-modal')
                this.clientAttachments.splice(index, 1)
                _.without(this.selectedAttachmentIds, attachmentId)
                EventBus.$emit('notification-event', {
                  'type'   : 'success',
                  'message': "Attachment deleted successfully"
                })
              })
            }
          }, {
            label : 'No',
            color : 'gray',
            action: () => {
              this.$modal.hide('confirm-modal')
            }
          }]
        })
      },


      validate() {
        let valid         = true
        this.toEmailError = null
        this.subjectError = null

        if(this.toEmail == null || this.toEmail.length == 0 || !validator.isEmail(this.toEmail)) {
          this.toEmailError = "Please type in a one valid email address"
          valid = false
        }

        if(this.ccEmails != null && this.ccEmails.length > 0) {

          // let emails = this.ccEmail.split(",")
          for(let i=0; i < this.ccEmails.length; i++) {
            this.ccEmails[i].error = null
            let e = this.ccEmails[i].email.trim()

            if(e != null && e.length > 0 && !validator.isEmail(e)) {
              this.ccEmails[i].error = "Please type in a valid email address."
              valid = false
            }
          }
        }

        if(this.subject == null || this.subject.length == 0) {
          this.subjectError = "Please type in a subject for this email"
          valid = false
        }

        return valid        
      },

      preview() {
        if(this.validate()) {
          this.apiPost({
            clientId       : this.$route.params.clientId,
            personalMessage: this.personalMessage,
            attachmentIds  : this.selectedAttachmentIds,
            subject        : this.subject,
            toEmail        : this.toEmail,
            ccEmail        : this.ccEmail
          }, 'submit/preview-data', (cacheKey) => {
            window.open(this.getBackendHost + '/api/submit/preview?cacheKey=' + cacheKey + '&token=' + this.getJwt, '_blank')
          })
        }
      },

      // submit for this should save everything for the final time
      // and then take the user to the next tab, which is assets
      submit() {
        if(this.validate()) {

          this.showLoader("Sending...")
          this.apiPost({
            clientId          : this.$route.params.clientId,
            personalMessage   : this.personalMessage,
            attachmentIds     : this.selectedAttachmentIds,
            subject           : this.subject,
            toEmail           : this.toEmail,
            ccEmails          : this.ccEmails,
            smsClient         : this.smsClient ? 1 : 0,
            smsBroker         : this.smsBroker ? 1 : 0,
            pages             : this.pages.length > 0 ? this.pages.join(",") : null,
            signElectronicLink: this.signElectronicLink
          }, 'submit/submit-by-broker', (data) => {          

            // is there a token? - then we need to open up a email client
            if(data.clientTokenId != null) {
              this.apiGet({
                clientId     : this.$route.params.clientId,
                clientTokenId: data.clientTokenId
              }, 'submit/mail-to-data', (data) => {
                this.hideLoader()

                // following will open the email client
                let mailToData       = data
                let convert_data     = this.decodeEntities(decodeURIComponent(mailToData.body));
                let new_data         = encodeURIComponent(convert_data);
                window.location.href = 'mailto:' + this.toEmail + '?subject=' + this.subject + '&body=' + new_data
              })
            }

            // otherwise, take them to sent page
            else {
              this.hideLoader()
              this.$router.push({
                name : 'application-submitted'
              })
            }

            window.localStorage.removeItem(this.broker.id + '-' + this.$route.params.clientId + '-pm')
          })
        }
      },

      decodeEntities(value) {
        if (!value) { 
          return ''; 
        }

        var hiddenPre = document.createElement("pre");
        hiddenPre.innerHTML = value.replace(/</g,"&lt;");
        // innerText depends on styling as it doesn't display hidden elements.
        // Therefore, it's better to use textContent not to cause unnecessary reflows.
        return hiddenPre.textContent;
      },

      resetPersonalMessage() {
        this.personalMessage = this.broker.personal_message
        EventBus.$emit('notification-event', {
          'type'   : 'success', 
          'message': 'Message reset successfully.'
        })
      }
    },

    metaInfo () {
      return {
        title: this.getClientPrimaryApplicant != null ? this.getApplicantName(this.getClientPrimaryApplicant) : "Brokerpad"
      }
    }
  }
</script>